/// <amd-module name="Wit/WorkItemForm/Header/WorkItemFormSubHeader" />
import * as React from "react";
import { ObservableArray, ObservableValue } from "VSS/Core/Observable";
import { format } from "VSS/Core/Util/String";
import { IdentityRef } from "VSS/Platform/Contracts/WebApi";
import { IVssKeyboardShortcutService } from "VSS/Platform/KeyboardShortcuts";
import { IVssComponentContext, VssComponent } from "VSS/Platform/Layout";
import { Ago } from "VSSUI/Ago";
import { DropdownExpandableTextField } from "VSSUI/Dropdown";
import { EditableDropdown } from "VSSUI/EditableDropdown";
import { Icon } from "VSSUI/Icon";
import { IListBoxItem } from "VSSUI/ListBox";
import { Observer } from "VSSUI/Observer";
import { Surface, SurfaceBackground } from "VSSUI/Surface";
import { TabBar, TabSize } from "VSSUI/Tabs";
import { TextField } from "VSSUI/TextField";
import { Tooltip } from "VSSUI/TooltipEx";
import { css, getSafeId } from "VSSUI/Util";
import { DropdownSelection } from "VSSUI/Utilities/DropdownSelection";
import { ISelectionRange } from "VSSUI/Utilities/Selection";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";
import { Control } from "Wit/Clients/Wit/Contracts/WorkItemFormLayout";
import { WorkItemClassificationNode } from "Wit/Clients/Wit/Contracts/WorkItemTracking";
import { FeatureFlags } from "Wit/Common/Constants/FeatureFlags";
import { CoreField, CoreFieldRefNames, DalFields } from "Wit/Common/Generated/Constants";
import { AreaPathDropdown } from "Wit/Common/WorkItemControls/AreaPathDropdown";
import { IterationDropdown } from "Wit/Common/WorkItemControls/IterationDropdown";
import { INodeTreeItem } from "Wit/Common/WorkItemControls/NodeTreeItemProvider";
import { convertNodesToListBoxItems } from "Wit/Common/WorkItemControls/NodeUtils";
import { IWorkItemNodeService } from "Wit/Common/WorkItemNodeService/IWorkItemNodeService";
import { WorkItemIdentityRef } from "Wit/Identity/Interfaces/WorkItemInterfaces";
import { useWorkItem } from "Wit/WorkItemForm/Common/Hooks";
import { getDateInUserTimeZone, tooltipTimeFormat } from "Wit/Common/DateUtils";
import * as Resources from "Wit/WorkItemForm/Resources";
import { IField, IWorkItem } from "Wit/WorkItemModel/OM/WorkItemInterfaces";
import { fieldHasList, getAllowedFieldValues, isReadOnly } from "Wit/WorkItemModel/WorkItem/FieldUtils";
import { StateCircle } from "../Common/Components";
import { MultiWorkItemFieldSubscriber } from "../Common/Components/WorkItemFieldSubscriber";

export interface IWorkItemFormSubHeaderProps {
    selectedTabId: ObservableValue<string>;
    systemControls: Control[];
    shortcutGroupName: string;
}

export function WorkItemFormSubHeader(props: IWorkItemFormSubHeaderProps) {
    const workItem = useWorkItem();
    const lastModifiedBy: WorkItemIdentityRef | IdentityRef = workItem.getFieldValueById(DalFields.LastChangedById, false, false, true);
    const lastChangedDate: Date | undefined | null = workItem.getFieldValueById(DalFields.LastChangedDateId);

    const stateControl = props.systemControls.find(control => control.id === CoreFieldRefNames.State);

    if (!stateControl) {
        console.error(Resources.StateControlMissingError);
    }

    const findSystemControlSlot = (refName: string) => {
        const controlSlot = props.systemControls.find(control => control.id === refName || control.replacesFieldReferenceName === refName);
        if (!controlSlot) {
            console.error(format(Resources.SystemControlMissingError, refName));
        }
        return controlSlot;
    };

    const reasonControlSlot = findSystemControlSlot(CoreFieldRefNames.Reason);

    const areaControlSlot = findSystemControlSlot(CoreFieldRefNames.AreaPath);

    const iterationControlSlot = findSystemControlSlot(CoreFieldRefNames.IterationPath);

    return (
        <Surface background={SurfaceBackground.neutral}>
            <div className="flex-row">
                <div className="work-item-form-subheader flex-row flex-grow flex-wrap padding-vertical-4 padding-horizontal-8 h-scroll-auto">
                    <div className="flex-column padding-left-8">
                        {stateControl && <WorkItemControl control={stateControl} workItem={workItem} shortcutGroupName={props.shortcutGroupName} />}
                        {reasonControlSlot && (
                            <WorkItemControl control={reasonControlSlot} workItem={workItem} shortcutGroupName={props.shortcutGroupName} />
                        )}
                    </div>
                    <div className="wif-node-dropdowns flex-column flex-grow padding-left-8 padding-vertical-4">
                        {areaControlSlot && (
                            <WorkItemControl control={areaControlSlot} workItem={workItem} shortcutGroupName={props.shortcutGroupName} />
                        )}
                        {iterationControlSlot && (
                            <WorkItemControl control={iterationControlSlot} workItem={workItem} shortcutGroupName={props.shortcutGroupName} />
                        )}
                    </div>
                    <div className="flex-column padding-left-8 flex-end flex-grow">
                        <div className="flex-row flex-grow body-s secondary-text" role="alert">
                            {lastModifiedBy && lastChangedDate ? (
                                <React.Fragment>
                                    {format(
                                        Resources.UpdatedBy,
                                        "identityRef" in lastModifiedBy ? lastModifiedBy.identityRef.displayName : lastModifiedBy.displayName
                                    )}
                                    &nbsp;
                                    <Ago
                                        date={getDateInUserTimeZone(lastChangedDate)}
                                        currentDate={getDateInUserTimeZone()}
                                        tooltipTimeFormat={tooltipTimeFormat}
                                    />
                                </React.Fragment>
                            ) : (
                                <br />
                            )}
                        </div>
                        <TabBar
                            className="wif-tabbar flex-self-end"
                            onSelectedTabChanged={(id: string) => (props.selectedTabId.value = id)}
                            tabSize={TabSize.Compact}
                        />
                    </div>
                </div>
            </div>
        </Surface>
    );
}

interface IWorkItemControlProps {
    control: Control;
    workItem: IWorkItem;
    shortcutGroupName: string;
}

class WorkItemControl extends VssComponent<IWorkItemControlProps> {
    private field: IField;
    private dropdownItems = new ObservableArray<IListBoxItem<WorkItemClassificationNode>>([]);
    private selection = new DropdownSelection();
    private fieldValue = new ObservableValue("");
    private readOnly = new ObservableValue<boolean>(false);
    private selectedNodePath = new ObservableValue<string>("");
    private controlRef = React.createRef<EditableDropdown<INodeTreeItem> | TextField>();
    private labelText = new ObservableValue<string | JSX.Element>("");
    private isStateDropdownBlockingWhenWITChangedEnabled: boolean = false;

    constructor(props: IWorkItemControlProps, context: IVssComponentContext) {
        super(props);

        this.context = context;
        this.field = props.workItem.getFieldByName(props.control.id)!;

        if (!this.field) {
            console.error(format(Resources.InvalidFieldError, this.props.control.id));
        }

        this.isStateDropdownBlockingWhenWITChangedEnabled = isFeatureFlagEnabled(
            context.pageContext,
            FeatureFlags.StateDropdownBlockingWhenWITChangedEnabled,
            false
        );

        this.refreshAllowedValues();
    }

    public componentDidMount() {
        this.labelText.value = this.props.control.label;

        const labelAmpersandIndex = this.props.control.label.indexOf("&");

        if (labelAmpersandIndex !== -1) {
            const shortcutKey = this.props.control.label.charAt(labelAmpersandIndex + 1);

            this.labelText.value = (
                <label accessKey={shortcutKey} htmlFor={getSafeId(getInputId(this.props.control.label))} id={getSafeId(this.props.control.label)}>
                    {this.props.control.label.substring(0, labelAmpersandIndex)}
                    <span className="wif-label-shortcut-char">{shortcutKey}</span>
                    {this.props.control.label.substring(labelAmpersandIndex + 2)}
                </label>
            );

            const shortcutService = this.context.pageContext.getService<IVssKeyboardShortcutService>("IVssKeyboardShortcutService");

            shortcutService.registerShortcut(this.props.shortcutGroupName, {
                description: format("Focus {0}", this.props.control.label.replace("&", "")), // CodeQL [SM02383] This method is not intended to be used with user input.
                combos: ["alt+" + shortcutKey.toLocaleLowerCase()],
                globalCombos: ["alt+" + shortcutKey.toLocaleLowerCase()],
                element: ".work-item-form-page",
                action: () => {
                    this.controlRef.current?.focus();
                }
            });
        }
    }

    public render() {
        if (!this.field) {
            return null;
        }

        const subscribers = [
            <MultiWorkItemFieldSubscriber
                key={`wif-${this.field.fieldDefinition.name}-subscriber`}
                fieldIds={[this.field.fieldDefinition.id, CoreField.TeamProject]}
                onUpdate={this.refreshAllowedValues}
            />
        ];

        return (
            <div className="flex-row flex-grow flex-center">
                {subscribers}
                <Observer readOnly={this.readOnly} selection={this.selection} selectedNodePath={this.selectedNodePath}>
                    {(observerProps: { readOnly: boolean; selection: ISelectionRange[] }) => {
                        const projectId = this.props.workItem.project.guid;
                        const beforeContent = [];
                        if (
                            (!this.props.workItem.isReadOnly() && observerProps.readOnly) ||
                            (this.isStateDropdownBlockingWhenWITChangedEnabled &&
                                this.field.fieldDefinition.id === DalFields.StateId &&
                                this.isWITypeChanged())
                        ) {
                            beforeContent.push(
                                <Icon
                                    key="readonly-icon"
                                    iconName="Lock"
                                    tooltipProps={{ text: format(Resources.ReadonlyFieldLockTooltip, this.field.fieldDefinition.name) }}
                                />
                            );
                        }
                        if (
                            this.field.fieldDefinition.id === DalFields.StateId &&
                            observerProps.selection.length &&
                            !(this.isStateDropdownBlockingWhenWITChangedEnabled && this.isWITypeChanged())
                        ) {
                            const selectedItem = this.dropdownItems.value[observerProps.selection[0].beginIndex]?.text ?? "";
                            beforeContent.push(<StateCircle key="state-circle" state={selectedItem} />);
                        }

                        const commonProps = {
                            ariaLabelledBy: this.props.control.label,
                            className: css("flex-grow work-item-control-control", observerProps.readOnly && "readOnly"),
                            disabled:
                                observerProps.readOnly ||
                                (this.field.fieldDefinition.id === DalFields.StateId &&
                                    this.isWITypeChanged() &&
                                    this.isStateDropdownBlockingWhenWITChangedEnabled),
                            inputId: getInputId(this.props.control.label),
                            placeHolder: this.props.control.watermark
                        };

                        const dropdownProps = {
                            items: this.dropdownItems,
                            onValueChange: this.onValueChange
                        };

                        let tooltipText = format(Resources.FieldNameTooltip, this.field.fieldDefinition.name);
                        const helpText = this.props.workItem.getTypeData().rules.fieldHelpTexts[this.field.fieldDefinition.id];
                        if (helpText) {
                            tooltipText = helpText + "\n" + tooltipText;
                        }

                        let control;
                        switch (this.field.fieldDefinition.id) {
                            case DalFields.AreaPathId:
                                control = (
                                    <AreaPathDropdown
                                        {...commonProps}
                                        {...dropdownProps}
                                        projectId={projectId}
                                        ref={this.controlRef as React.Ref<EditableDropdown<INodeTreeItem>>}
                                        selectedText={this.selectedNodePath}
                                    />
                                );
                                break;
                            case DalFields.IterationPathId:
                                control = (
                                    <IterationDropdown
                                        {...commonProps}
                                        {...dropdownProps}
                                        projectId={projectId}
                                        ref={this.controlRef as React.Ref<EditableDropdown<INodeTreeItem>>}
                                        selectedText={this.selectedNodePath}
                                    />
                                );
                                break;
                            default:
                                const isDropdown = fieldHasList(this.field);
                                control = isDropdown ? (
                                    <EditableDropdown
                                        {...commonProps}
                                        {...dropdownProps}
                                        allowTextSelection
                                        ref={this.controlRef as React.Ref<EditableDropdown<INodeTreeItem>>}
                                        renderExpandable={props => (
                                            <DropdownExpandableTextField
                                                {...props}
                                                disabled={
                                                    observerProps.readOnly ||
                                                    (this.isStateDropdownBlockingWhenWITChangedEnabled &&
                                                        this.field.fieldDefinition.id === DalFields.StateId &&
                                                        this.isWITypeChanged())
                                                }
                                            />
                                        )}
                                        selectedText={this.fieldValue}
                                    />
                                ) : (
                                    <TextField
                                        {...commonProps}
                                        value={this.fieldValue}
                                        onChange={(e, newValue) => this.onValueChange({ id: newValue, text: newValue })}
                                        ref={this.controlRef as React.Ref<TextField>}
                                        ariaLabel={Resources.ReasonField}
                                    />
                                );
                        }

                        return (
                            <div className={css("flex-row flex-grow", !this.props.control.visible && "invisible")}>
                                <Observer labelText={this.labelText}>
                                    {() => (
                                        <Tooltip className="work-item-form-control-tooltip" text={tooltipText}>
                                            <div className="work-item-control-label flex-row flex-center body-s padding-right-4 secondary-text">
                                                {this.labelText.value}
                                            </div>
                                        </Tooltip>
                                    )}
                                </Observer>
                                <div className="work-item-control-before-content flex-row flex-center">{beforeContent}</div>
                                {control}
                            </div>
                        );
                    }}
                </Observer>
            </div>
        );
    }

    public refreshAllowedValues = async (): Promise<void> => {
        // make sure we have the updated field in case the project changed
        this.field = this.props.workItem.getFieldByName(this.props.control.id)!;
        if (!this.field) {
            return;
        }

        this.fieldValue.value = this.props.workItem.getFieldValueById(this.field.fieldDefinition.id);
        const nodeService = this.context.pageContext.getService<IWorkItemNodeService>("IWorkItemNodeService");
        let dropdownItems: IListBoxItem<WorkItemClassificationNode>[] = [];
        let setSelection = true;
        switch (this.field.fieldDefinition.id) {
            case DalFields.AreaPathId:
                setSelection = false;
                const areaPathNodes = await nodeService.getAreaPathNodes(this.props.workItem.project.guid);
                if (areaPathNodes) {
                    dropdownItems = convertNodesToListBoxItems(areaPathNodes);
                }
                break;
            case DalFields.IterationPathId:
                setSelection = false;
                const iterationPathNodes = await nodeService.getIterationNodes(this.props.workItem.project.guid);
                if (iterationPathNodes) {
                    dropdownItems = convertNodesToListBoxItems(iterationPathNodes);
                }
                break;
            default:
                const items = getAllowedFieldValues(this.field, this.props.workItem).value;

                dropdownItems = items.map(item => {
                    return { id: item, text: item };
                });
        }

        const selectedItem = this.props.workItem.getFieldValueById(this.field.fieldDefinition.id);
        const originalItem = this.props.workItem.getFieldValueById(this.field.fieldDefinition.id, true);

        let selectedIndex = -1;

        if (originalItem || selectedItem) {
            if (dropdownItems.length) {
                selectedIndex = dropdownItems.findIndex(item => {
                    return item.text?.localeCompare(selectedItem, navigator.language) === 0;
                });

                if (selectedIndex === -1) {
                    selectedIndex = dropdownItems.findIndex(item => {
                        return item.text?.localeCompare(originalItem, navigator.language) === 0;
                    });
                }
            } else if (fieldHasList(this.field)) {
                // If this is a dropdown with no allowed values, set to the original value
                dropdownItems.push({ id: originalItem, text: originalItem });
                if (selectedItem !== originalItem) {
                    this.props.workItem.setFieldValueById(this.field.fieldDefinition.id, originalItem);
                }
                selectedIndex = 0;
            }

            this.dropdownItems.value = dropdownItems;

            if (setSelection) {
                this.selection.select(selectedIndex === -1 ? 0 : selectedIndex);
            } else if (this.selectedNodePath.value === undefined || this.selectedNodePath.value.localeCompare(selectedItem, navigator.language) !== 0) {
                this.selectedNodePath.value = selectedItem;
            }
        } else {
            this.dropdownItems.value = dropdownItems;
        }

        this.readOnly.value = isReadOnly(this.field);
    };

    private onValueChange = (value: IListBoxItem<WorkItemClassificationNode> | undefined) => {
        switch (this.field.fieldDefinition.id) {
            // Set the Ids rather than the strings
            case DalFields.AreaPathId:
                this.props.workItem.setFieldValueById(DalFields.AreaIDId, value?.data?.id);
                break;
            case DalFields.IterationPathId:
                this.props.workItem.setFieldValueById(DalFields.IterationIDId, value?.data?.id);
                break;
            default:
                this.props.workItem.setFieldValueById(this.field.fieldDefinition.id, value?.text);
        }
    };

    private isWITypeChanged = (): boolean => {
        return this.props.workItem.isDirty() && this.props.workItem.manuallySetFields?.has(DalFields.WorkItemTypeId) && !this.props.workItem.isNew();
    };
}

function getInputId(controlName: string): string {
    return `${controlName}-input`;
}
